import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useClickOutside } from '../hooks/useClickOutside';
import StarIcon from './icons/StarIcon';
import { ViewIcon } from './icons/ViewIcon';
import { ChecklistIcon } from './icons/ChecklistIcon';
import { BookmarkIcon } from './icons/BookmarkIcon';
import EditIcon from './icons/EditIcon'; // Import EditIcon
import ProjectMenu from './project/ProjectMenu';
import ProjectMetaInfo from './project/ProjectMetaInfo';
import { makeLinksClickable, getContrastYIQ } from '../utils/textUtils';
import {
    Card,
    StarButton,
    ViewButton,
    ProjectTitle,
    ProjectTagline,
    TodoList,
    TodoItem,
    TodoCheckbox,
    TodoText,
    DragHandle,
    MenuButton,
    MenuDropdown,
    MenuItem,
    BookmarkUrl,
    // UrlIcon, // Will be replaced by styles from ProjectPage.styles.js or similar
    UrlText,
    // Adapting styles similar to ProjectHeader for URL editing
    StyledProjectUrlInput,
    StyledProjectUrlDisplay,
    StyledUrlWrapper,
    StyledEditButton,
    ProjectLinkIcon, // Using ProjectLinkIcon instead of UrlIcon from ProjectCard.styles.js
} from '../styles/ProjectCard.styles';

export const ViewType = {
    TAGLINE: 'tagline',
    CHECKLIST: 'checklist',
    BOOKMARK: 'bookmark'
};

// No longer using local formatUrl, will display full URL or placeholder
// const formatUrl = (url) => {
//     if (!url) return '';
//     try {
//         const urlObj = new URL(url);
//         return urlObj.hostname + urlObj.pathname;
//     } catch {
//         return url;
//     }
// };

const ProjectCard = ({ project, onToggleStar, isStarred, onArchive, onCardClick, onDuplicate }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [showMenu, setShowMenu] = useState(false);
    const [viewType, setViewType] = useState(ViewType.TAGLINE);
    const menuRef = React.useRef();
    const hasBackground = !!project.background?.urls?.thumb;
    const textColor = project.background?.color && !project.background?.urls?.thumb ? getContrastYIQ(project.background.color) : null;


    // States for URL editing
    const [isUrlEditing, setIsUrlEditing] = useState(false);
    const [localUrl, setLocalUrl] = useState(project.url || '');
    const urlInputRef = useRef(null); // Ref for the URL input field

    useClickOutside(menuRef, () => setShowMenu(false));

    useEffect(() => {
        setLocalUrl(project.url || ''); // Update localUrl when project.url changes
    }, [project.url]);

    useEffect(() => {
        // Focus input when entering edit mode
        if (isUrlEditing && urlInputRef.current) {
            urlInputRef.current.focus();
        }
    }, [isUrlEditing]);

    useEffect(() => {
        const loadViewPreference = async () => {
            try {
                const userSettingsRef = doc(db, 'userSettings', user.uid);
                const userSettingsDoc = await getDoc(userSettingsRef);

                if (userSettingsDoc.exists()) {
                    const data = userSettingsDoc.data();
                    const projectViews = data.projectViews || {};
                    setViewType(projectViews[project.id] || ViewType.TAGLINE);
                } else {
                    await setDoc(userSettingsRef, {
                        projectViews: {},
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString()
                    });
                }
            } catch (error) {
                console.error('Error loading view preference:', error);
            }
        };

        loadViewPreference();
    }, [user.uid, project.id]);

    const totalCards = Object.values(project.columns || {}).reduce(
        (total, column) => total + (column.cards?.length || 0),
        0
    );

    const totalTodos = project.todos?.length || 0;
    const completedTodos = project.todos?.filter(todo => todo.completed).length || 0;

    const isValidUrl = (string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    const handleCardClick = (e) => {
        // Prevent card click if editing URL or clicking on the URL itself
        if (isUrlEditing || (e.target.closest('a') && viewType === ViewType.BOOKMARK)) {
            return;
        }
        e.preventDefault();
        onCardClick(project, viewType);
    };

    const handleUrlChange = (e) => {
        setLocalUrl(e.target.value);
    };

    const handleUrlBlur = async () => {
        if (localUrl !== project.url) {
            if (!localUrl || isValidUrl(localUrl)) {
                try {
                    const projectRef = doc(db, 'projects', project.id);
                    await updateDoc(projectRef, { url: localUrl });
                } catch (error) {
                    console.error("Error updating project URL:", error);
                    setLocalUrl(project.url || ''); // Revert on error
                }
            } else {
                 // Optionally, provide feedback for invalid URL
                console.warn("Invalid URL, not saving:", localUrl);
                setLocalUrl(project.url || ''); // Revert to original if invalid and different
            }
        }
        setIsUrlEditing(false);
    };

    const handleUrlEditClick = (e) => {
        e.stopPropagation(); // Prevent card click
        setIsUrlEditing(true);
    };

    const handleUrlKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleUrlBlur();
        } else if (e.key === 'Escape') {
            setLocalUrl(project.url || ''); // Revert changes
            setIsUrlEditing(false);
        }
    };


    const handleStarClick = (e) => {
        e.stopPropagation();
        onToggleStar();
    };

    const handleViewToggle = async (e) => {
        e.stopPropagation();
        // If currently editing URL in bookmark view, save before toggling
        if (isUrlEditing && viewType === ViewType.BOOKMARK) {
            await handleUrlBlur();
        }

        const nextViewType = {
            [ViewType.TAGLINE]: ViewType.CHECKLIST,
            [ViewType.CHECKLIST]: ViewType.BOOKMARK,
            [ViewType.BOOKMARK]: ViewType.TAGLINE,
        }[viewType];

        setViewType(nextViewType);

        try {
            const userSettingsRef = doc(db, 'userSettings', user.uid);
            await updateDoc(userSettingsRef, {
                [`projectViews.${project.id}`]: nextViewType,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error updating view preference:', error);
            setViewType(viewType); // Revert on error
        }
    };

    const handleMenuClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowMenu(!showMenu);
    };

    const handleArchiveClick = (e) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to archive this project?')) {
            onArchive(project.id);
        }
        setShowMenu(false);
    };

    const handleDuplicateClick = (e) => {
        e.stopPropagation();
        onDuplicate(project.id);
        setShowMenu(false);
    };

    const getViewIcon = () => {
        switch (viewType) {
            case ViewType.CHECKLIST:
                return <ChecklistIcon />;
            case ViewType.BOOKMARK:
                return <BookmarkIcon />;
            default:
                return <ViewIcon />;
        }
    };

    const renderContent = () => {
        switch (viewType) {
            case ViewType.CHECKLIST:
                return (
                    <TodoList hasBackground={hasBackground}>
                        {project.todos && project.todos.length > 0 ? (
                            project.todos
                                .filter(todo => !todo.completed)
                                .concat(project.todos.filter(todo => todo.completed))
                                .slice(0, 3)
                                .map((todo, index) => (
                                    <TodoItem key={todo.id || index} completed={todo.completed} textColor={textColor}>
                                        <TodoCheckbox checked={todo.completed} textColor={textColor} />
                                        <TodoText>{todo.text}</TodoText>
                                    </TodoItem>
                                ))
                        ) : (
                            <TodoItem textColor={textColor}>No todos yet</TodoItem>
                        )}
                    </TodoList>
                );
            case ViewType.BOOKMARK:
                if (isUrlEditing || !localUrl) {
                    return (
                        <StyledUrlWrapper hasBackground={hasBackground} isEditing={isUrlEditing} textColor={textColor}>
                            <ProjectLinkIcon textColor={textColor}/>
                            <StyledProjectUrlInput
                                ref={urlInputRef}
                                type="text"
                                value={localUrl}
                                onChange={handleUrlChange}
                                onBlur={handleUrlBlur}
                                onKeyDown={handleUrlKeyDown}
                                placeholder="Add project URL..."
                                hasBackground={hasBackground}
                                textColor={textColor}
                            />
                        </StyledUrlWrapper>
                    );
                }
                return (
                    <StyledUrlWrapper hasBackground={hasBackground} textColor={textColor}>
                        <ProjectLinkIcon textColor={textColor}/>
                        <StyledProjectUrlDisplay
                            href={isValidUrl(localUrl) ? localUrl : '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                if (!isValidUrl(localUrl)) e.preventDefault();
                            }}
                            hasBackground={hasBackground}
                            title={localUrl}
                            textColor={textColor}
                        >
                            {localUrl}
                        </StyledProjectUrlDisplay>
                        <StyledEditButton onClick={handleUrlEditClick} hasBackground={hasBackground} textColor={textColor}>
                            <EditIcon />
                        </StyledEditButton>
                    </StyledUrlWrapper>
                );
            default:
                return (
                    <ProjectTagline hasBackground={hasBackground} textColor={textColor}>
                        {makeLinksClickable(project.tagline)}
                    </ProjectTagline>
                );
        }
    };

    return (
        <>
            <Card
                onClick={handleCardClick}
                background={project.background?.urls?.thumb}
                backgroundColor={project.background?.color}
                // Clickable state is now handled by the handleCardClick logic
                // isClickable={viewType === ViewType.BOOKMARK && !!project.url}
            >
                <DragHandle title="Drag to reorder" />
                <StarButton
                    onClick={handleStarClick}
                    isStarred={isStarred}
                    title={isStarred ? "Remove from focused" : "Add to focused"}
                >
                    <StarIcon />
                </StarButton>

                <ViewButton
                    onClick={handleViewToggle}
                    isActive={viewType !== ViewType.TAGLINE}
                    title={`Switch to ${viewType === ViewType.TAGLINE ? "checklist" :
                            viewType === ViewType.CHECKLIST ? "bookmark" : "description"
                        } view`}
                >
                    {getViewIcon()}
                </ViewButton>

                <span ref={menuRef}>
                    <ProjectMenu
                        showMenu={showMenu}
                        onMenuClick={handleMenuClick}
                        onArchiveClick={handleArchiveClick}
                        onDuplicateClick={handleDuplicateClick}
                    />
                </span>

                <ProjectTitle hasBackground={hasBackground} title={project.title} textColor={textColor}>{project.title}</ProjectTitle>
                {renderContent()}
                <ProjectMetaInfo
                    project={project}
                    hasBackground={hasBackground}
                    textColor={textColor}
                    totalCards={totalCards}
                    totalTodos={totalTodos}
                    completedTodos={completedTodos}
                />
            </Card>
        </>
    );
};

export default ProjectCard;
